import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { authApi } from "@/api/auth.api";
import { userDataApi } from "@/api/user-data.api";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { CountrySelect } from "@/components/ui/country-select";
import { useApp } from "@/hooks/use-app-context";
import { cn } from "@/lib/utils";
import { toProfileFields } from "@/lib/profile-fields";
import { profileCompletionCopy } from "./profile-completion-copy";
import {
  type Gender,
  isProfileCompletionDeferredOn,
  normalizeGender,
  pendingProfileFields,
} from "./profile-completion";

/**
 * Holds every signed-in account, whatever its role, until the personal fields
 * the backend requires are filled (spec 028). It cannot be dismissed: the
 * only other way out is to sign out.
 */
export default function ProfileCompletionGate() {
  const { user, setUser, language, t } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const copy = profileCompletionCopy[language === "en" ? "en" : "fr"];

  const pending = pendingProfileFields(user);
  const checkedFor = useRef<string | null>(null);

  // A session stored before the check existed carries no list: ask once.
  useEffect(() => {
    if (!user?.id || pending !== undefined || checkedFor.current === user.id) return;
    checkedFor.current = user.id;
    authApi
      .getCurrentUser()
      .then((fresh) => {
        if (fresh && String(fresh.id) === String(user.id)) {
          setUser({ ...user, ...fresh } as Parameters<typeof setUser>[0]);
        }
      })
      .catch(() => {
        // Offline or expired: the next sign-in carries the list anyway.
      });
  }, [user, pending, setUser]);

  const [gender, setGender] = useState<Gender | null>(null);
  const [country, setCountry] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setGender(normalizeGender(user?.gender));
    setCountry(user?.country ?? "");
    setProblem(null);
  }, [user?.id, user?.gender, user?.country]);

  const open =
    Boolean(user?.id) &&
    !user?.mustChangePassword &&
    Boolean(pending?.length) &&
    !isProfileCompletionDeferredOn(location.pathname);

  if (!open || !user || !pending) return null;

  const asksGender = pending.includes("gender");
  const asksCountry = pending.includes("country");

  const save = async () => {
    if ((asksGender && !gender) || (asksCountry && !country.trim())) {
      setProblem(copy.required);
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      const dto = {
        ...(asksGender && gender ? { gender } : {}),
        ...(asksCountry ? { country: country.trim() } : {}),
      };
      const saved = await userDataApi.updateUser(user.username || user.id, dto);
      const record = (saved && typeof saved === "object" && "data" in saved
        ? (saved as { data: unknown }).data
        : saved) as Record<string, unknown> | null;
      setUser({
        ...user,
        ...dto,
        missingProfileFields: toProfileFields(record?.missingProfileFields) ?? [],
        requiresProfileCompletion: record?.requiresProfileCompletion === true,
      });
    } catch {
      setProblem(copy.error);
    } finally {
      setSaving(false);
    }
  };

  const signOut = () => {
    setUser(null);
    navigate(`/${language}/login`, { replace: true });
  };

  const genders: { value: Gender; label: string }[] = [
    { value: "female", label: t.female },
    { value: "male", label: t.male },
  ];

  return (
    <AlertDialog open>
      <AlertDialogContent className="w-[calc(100%-2rem)] max-w-md gap-5 rounded-2xl p-6 sm:rounded-2xl">
        <AlertDialogHeader className="space-y-2 text-left">
          <AlertDialogTitle className="text-lg font-semibold leading-snug tracking-tight">
            {copy.title}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm leading-6">{copy.intro}</AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4">
          {asksGender && (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">{copy.gender}</legend>
              <div role="radiogroup" aria-label={copy.gender} className="grid grid-cols-2 gap-2">
                {genders.map((option) => {
                  const selected = gender === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setGender(option.value)}
                      className={cn(
                        "h-11 rounded-lg border text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        selected
                          ? "border-primary bg-primary/10 text-foreground"
                          : "border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground",
                      )}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )}

          {asksCountry && (
            <div className="space-y-2">
              <label htmlFor="profile-completion-country" className="text-sm font-medium">
                {copy.country}
              </label>
              <CountrySelect
                id="profile-completion-country"
                value={country}
                onChange={(next) => setCountry(next.name)}
                lang={language}
                placeholder={copy.countryPlaceholder}
                searchPlaceholder={copy.countrySearch}
                emptyLabel={copy.countryNotFound}
              />
            </div>
          )}

          {problem && (
            <p role="alert" className="text-sm text-destructive">
              {problem}
            </p>
          )}
        </div>

        <AlertDialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={signOut} disabled={saving} className="w-full sm:w-auto">
            {copy.logout}
          </Button>
          <Button onClick={save} disabled={saving} className="w-full sm:w-auto">
            {saving ? copy.saving : copy.save}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
